<template>
  <div class="pt-6 pb-2">
    <h3 class="mb-3">
      {{ $t('form.questionnaire.questionnaireGroups.title') }}
    </h3>
    <v-expansion-panels
      multiple
      readonly
      class="mb-4"
    >
      <v-expansion-panel
        v-for="(questionnaireGroupVariants, id) in questionnaireGroupsVariants"
        :key="id"
        :ref="`questionanire-group-${id}`"
        class="platform--expansion-panel mt-4 rounded"
      >
        <v-expansion-panel-header @click="toggleCriteriaItemExpansionPanel(id)">
          <div class="min-width-0">
            <v-row no-gutters align="center" class="flex-nowrap">
              <v-col class="flex-grow-1 min-width-0">
                <div class="questionnaire-group--header pr-6">
                  <strong>
                    <template v-if="questionnaireGroupVariants[selectedQuestionnaireVariant]">
                      {{ questionnaireGroupVariants[selectedQuestionnaireVariant].name || $t('form.questionnaire.customContent.unnamedItem') }}
                    </template>
                  </strong>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <questionnaire-group-form
            v-model="questionnaireGroupsVariants[id]"
            :selected-questionnaire-variant="selectedQuestionnaireVariant"
            :variants="variants"
            :questionnaire-id="questionnaireId"
            :disabled="true"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script lang="ts">
import { getQuestionnaireGroupVariantData } from '@/helpers/questionnaire'
import Vue, { PropType } from 'vue'
import {
  PlatformQuestionnaireVariant,
  QuestionnaireVariantCode
} from '@/types/Platform'
import { CriteriaItem } from '@betterboards/shared/types/Content'
import QuestionnaireGroupForm from '@/components/forms/questionnaire-groups/QuestionnaireGroupForm.vue'
import { QuestionnaireGroup } from '@betterboards/shared/types/Questionnaire'

interface QuestionnaireGroupsMap {
  [questionnaireGroupId: string]: {
    [variantCode: string]: CriteriaItem | null
  } | undefined
}

export default Vue.extend({
  name: 'QuestionnaireGroupsList',
  components: {
    QuestionnaireGroupForm
  },
  props: {
    value: { type: Array as PropType<QuestionnaireGroup[]>, required: true },
    selectedQuestionnaireVariant: { type: String as PropType<QuestionnaireVariantCode>, required: false },
    variants: { type: Array as PropType<PlatformQuestionnaireVariant[]>, required: true },
    questionnaireId: { type: String, required: false },
    disabled: { type: Boolean, default: false }
  },
  data: () => ({
    questionnaireGroupsVariantsMap: <QuestionnaireGroupsMap>{}
  }),
  mounted (): void {
    this.init()
  },
  computed: {
    defaultVariant (): QuestionnaireVariantCode | undefined {
      return this.variants?.[0]?.variantCode as QuestionnaireVariantCode | undefined
    },
    questionnaireGroupsVariants (): QuestionnaireGroupsMap {
      return Object.keys(this.questionnaireGroupsVariantsMap).reduce(
        (map, slug) => {
          if (this.questionnaireGroupsVariantsMap[slug]) {
            map[slug] = this.questionnaireGroupsVariantsMap[slug]
          }
          return map
        },
        {}
      )
    }
  },
  methods: {
    init (): void {
      if (!this.value?.length) {
        return
      }
      this.value.forEach((questionnaireGroup: QuestionnaireGroup) => {
        questionnaireGroup.variantCode = questionnaireGroup.variantCode ?? this.defaultVariant
        this.setQuestionnaireGroup(questionnaireGroup)
      })
    },
    setQuestionnaireGroup (questionnaireGroup: QuestionnaireGroup): void {
      if (!this.questionnaireGroupsVariantsMap[questionnaireGroup.id]) {
        const questionnaireGroupVariants = this.variants.reduce((map, v) => {
          map[v.variantCode] = getQuestionnaireGroupVariantData(questionnaireGroup, v.variantCode)
          return map
        }, {})
        Vue.set(this.questionnaireGroupsVariantsMap, questionnaireGroup.id, questionnaireGroupVariants)
      }
      Vue.set(this.questionnaireGroupsVariantsMap[questionnaireGroup.id]!, questionnaireGroup.variantCode, questionnaireGroup)
    },
    toggleCriteriaItemExpansionPanel (slug?: string): void {
      if (!slug) {
        return
      }
      const sectionEl = this.$refs[`questionanire-group-${slug}`]?.[0]
      if (sectionEl) {
        sectionEl.toggle()
      }
    }
  }
})
</script>

<style lang="scss" scoped>

.questionnaire-group--header {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
